import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Receipt, Plus, Search, Edit, Trash2, Eye, X,
  DollarSign, TrendingUp, Calendar, CreditCard, FileText
} from 'lucide-react';

interface Customer {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface Customer {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface ReceiptType {
  id: number;
  receiptNumber: string;
  amount: number;
  customerName?: string;
  paymentMethod?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export default function ReceiptsPage() {
  const [receipts, setReceipts] = useState<ReceiptType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState<ReceiptType | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptType | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [form, setForm] = useState({
    amount: '', customerName: '', paymentMethod: 'CASH', notes: ''
  });

  useEffect(() => {
    loadReceipts();
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await api.get('/api/customers?pageSize=100');
      if (response.data.success) {
        const data = response.data.data;
        setCustomers(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/receipts?pageSize=100');
      if (response.data.success) {
        const data = response.data.data;
        setReceipts(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingReceipt(null);
    setForm({ amount: '', customerName: '', paymentMethod: 'CASH', notes: '' });
    setShowModal(true);
  };

  const openEditModal = (receipt: ReceiptType) => {
    setEditingReceipt(receipt);
    setForm({
      amount: receipt.amount?.toString() || '',
      customerName: receipt.customerName || '',
      paymentMethod: receipt.paymentMethod || 'CASH',
      notes: receipt.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(form.amount) || 0,
        customerName: form.customerName,
        paymentMethod: form.paymentMethod,
        notes: form.notes
      };
      if (editingReceipt) {
        await api.put('/api/receipts/' + editingReceipt.id, payload);
        alert('تم تحديث سند القبض بنجاح');
      } else {
        await api.post('/api/receipts', payload);
        alert('تم إضافة سند القبض بنجاح');
      }
      setShowModal(false);
      loadReceipts();
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حفظ السند');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السند؟')) return;
    try {
      await api.delete('/api/receipts/' + id);
      loadReceipts();
      alert('تم حذف السند بنجاح');
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حذف السند');
    }
  };

  const filteredReceipts = Array.isArray(receipts) ? receipts.filter(r =>
    (r.receiptNumber || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.customerName || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.paymentMethod || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: receipts.length,
    totalAmount: receipts.reduce((sum, r) => sum + (r.amount || 0), 0),
    cashAmount: receipts.filter(r => r.paymentMethod === 'CASH').reduce((sum, r) => sum + (r.amount || 0), 0),
    bankAmount: receipts.filter(r => r.paymentMethod === 'BANK').reduce((sum, r) => sum + (r.amount || 0), 0)
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition';

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CASH': return 'نقدي';
      case 'BANK': return 'تحويل بنكي';
      case 'CHECK': return 'شيك';
      case 'CARD': return 'بطاقة ائتمان';
      default: return method || '-';
    }
  };

  const getPaymentMethodStyle = (method: string) => {
    switch (method) {
      case 'CASH': return 'bg-green-50 text-green-700 border-green-200';
      case 'BANK': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'CHECK': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'CARD': return 'bg-orange-50 text-orange-700 border-orange-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <Receipt className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>سندات القبض</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة سندات القبض والتحصيل المالي</p>
            </div>
          </h1>
        </div>
        <Button
          onClick={openCreateModal}
          className='bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
        >
          <Plus className='w-5 h-5 ml-2' />
          <span>إضافة سند قبض</span>
        </Button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Receipt className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي السندات</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <DollarSign className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي المبالغ</p>
            <p className='text-2xl font-bold text-green-700'>{stats.totalAmount.toLocaleString()} ,.E</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <CreditCard className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>المبالغ النقدية</p>
            <p className='text-2xl font-bold text-blue-700'>{stats.cashAmount.toLocaleString()} ,.E</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <TrendingUp className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>المبالغ البنكية</p>
            <p className='text-2xl font-bold text-orange-700'>{stats.bankAmount.toLocaleString()} ,.E</p>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث برقم السند، اسم العميل، أو طريقة الدفع...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل السندات...</p>
            </div>
          ) : filteredReceipts.length === 0 ? (
            <div className='p-12 text-center'>
              <Receipt className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا توجد سندات</h3>
              <p className='text-gray-500'>ابدأ بإضافة سند القبض الأول</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>رقم السند</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>العميل</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>المبلغ</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>طريقة الدفع</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>التاريخ</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((receipt) => (
                    <tr key={receipt.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                      <td className='py-4 px-5'>
                        <span className='font-mono font-semibold text-orange-600'>{receipt.receiptNumber}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-gray-900'>{receipt.customerName || '-'}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-bold text-green-600'>{(receipt.amount || 0).toLocaleString()} ,.E</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold border ' + getPaymentMethodStyle(receipt.paymentMethod || '')}>
                          {getPaymentMethodLabel(receipt.paymentMethod || '')}
                        </span>
                      </td>
                      <td className='py-4 px-5 text-gray-700'>
                        {receipt.createdAt ? new Date(receipt.createdAt).toLocaleDateString('ar-EG') : '-'}
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1'>
                          <button
                            onClick={() => { setSelectedReceipt(receipt); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => openEditModal(receipt)}
                            className='p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition'
                            title='تعديل'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(receipt.id)}
                            className='p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition'
                            title='حذف'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>
                  {editingReceipt ? 'تعديل سند القبض' : 'إضافة سند قبض جديد'}
                </h2>
                <button onClick={() => setShowModal(false)} className='p-2 hover:bg-gray-100 rounded-lg transition'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>المبلغ (ج.م) *</Label>
                    <Input
                      type='number'
                      step='0.01'
                      value={form.amount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, amount: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='0.00'
                    />
                  </div>
                  <div>
                    <Label>اسم العميل</Label>
                    <select
                      value={form.customerName}
                      onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                      className={inputClass}
                    >
                      <option value=''>اختر العميل</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.name}>{c.name} {c.phone ? '(' + c.phone + ')' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className='md:col-span-2'>
                    <Label>طريقة الدفع</Label>
                    <select
                      value={form.paymentMethod}
                      onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                      className={inputClass}
                    >
                      <option value='CASH'>نقدي</option>
                      <option value='BANK'>تحويل بنكي</option>
                      <option value='CHECK'>شيك</option>
                      <option value='CARD'>بطاقة ائتمان</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>ملاحظات</Label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className={inputClass}
                    placeholder='أضف ملاحظات حول السند...'
                    rows={3}
                  />
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>إلغاء</Button>
                  <Button type='submit' className='flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'>
                    {editingReceipt ? 'تحديث السند' : 'إضافة السند'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedReceipt && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل سند القبض</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center gap-4 pb-4 border-b'>
                  <div className='w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white'>
                    <Receipt className='w-8 h-8' />
                  </div>
                  <div>
                    <h3 className='text-xl font-bold text-orange-600'>{selectedReceipt.receiptNumber}</h3>
                    <p className='text-sm text-gray-500'>
                      {selectedReceipt.createdAt ? new Date(selectedReceipt.createdAt).toLocaleDateString('ar-EG') : '-'}
                    </p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_اسم العميل</p>
                    <p className='font-semibold'>{selectedReceipt.customerName || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المبلغ (ج.م) *</p>
                    <p className='font-semibold text-green-600 text-xl'>{(selectedReceipt.amount || 0).toLocaleString()} ,.E</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_طريقة الدفع</p>
                    <p className='font-semibold'>{getPaymentMethodLabel(selectedReceipt.paymentMethod || '')}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>التاريخ</p>
                    <p className='font-semibold'>{selectedReceipt.createdAt ? new Date(selectedReceipt.createdAt).toLocaleDateString('ar-EG') : '-'}</p>
                  </div>
                  {selectedReceipt.notes && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500'>VIEW_ملاحظات</p>
                      <p className='font-semibold'>{selectedReceipt.notes}</p>
                    </div>
                  )}
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedReceipt); }} className='flex-1 bg-orange-500 hover:bg-orange-600'>
                    <Edit className='w-4 h-4 ml-2' />
                    تعديل السند
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant='outline' className='flex-1'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
